import React, { useState } from 'react';
import { X, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export const TrainingRegistrationForm = ({ training, isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    organization: '',
    designation: '',
    experience_years: '',
    special_requirements: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState('form'); // 'form', 'processing', 'success', 'error'
  const [successData, setSuccessData] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (!formData.full_name || formData.full_name.length < 2) {
      setError('Please enter your full name');
      return false;
    }
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    if (!formData.phone || formData.phone.length < 10) {
      setError('Please enter a valid phone number');
      return false;
    }
    return true;
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayment = async (orderData) => {
    // Load Razorpay SDK
    const res = await loadRazorpayScript();

    if (!res) {
      setError('Failed to load payment gateway. Please try again.');
      setStep('error');
      return;
    }

    // Razorpay payment options
    const options = {
      key: orderData.razorpay_key_id,
      amount: orderData.amount,
      currency: orderData.currency,
      name: 'ISDE Academy',
      description: training.title,
      order_id: orderData.order_id,
      handler: async function (response) {
        try {
          setStep('processing');

          // Verify payment on backend
          const verifyResponse = await fetch(`${API_URL}/api/payment/verify-payment`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              registration_id: orderData.registration_id
            }),
          });

          const verifyData = await verifyResponse.json();

          if (verifyResponse.ok && verifyData.success) {
            setSuccessData(verifyData);
            setStep('success');
            if (onSuccess) {
              onSuccess(verifyData);
            }

            // Auto-close modal after payment success so user sees confirmation briefly
            setTimeout(() => {
              try { onClose(); } catch (e) { /* ignore */ }
            }, 3000);
          } else {
            throw new Error(verifyData.detail || 'Payment verification failed');
          }
        } catch (error) {
          console.error('Payment verification error:', error);
          setError(error.message || 'Payment verification failed. Please contact support.');
          setStep('error');
        }
      },
      prefill: {
        name: formData.full_name,
        email: formData.email,
        contact: formData.phone
      },
      notes: {
        training_id: training.id,
        training_title: training.title
      },
      theme: {
        color: '#3B82F6'
      },
      modal: {
        ondismiss: async function() {
          // User closed the payment modal
          try {
            await fetch(`${API_URL}/api/payment/payment-failed`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                registration_id: orderData.registration_id,
                reason: 'User closed payment modal'
              }),
            });
          } catch (error) {
            console.error('Error recording payment cancellation:', error);
          }

          setError('Payment was cancelled. You can try again.');
          setStep('error');
        }
      }
    };

    const paymentObject = new window.Razorpay(options);
    paymentObject.open();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setStep('processing');

    try {
      // Check if training is paid or complimentary
      if (!training.is_paid) {
        // Free registration - just submit to backend
        const response = await fetch(`${API_URL}/api/public/training-registration`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...formData,
            training_id: training.id,
            experience_years: formData.experience_years ? parseInt(formData.experience_years) : null
          }),
        });

        const data = await response.json();

        if (response.ok) {
          // Free registration - create simple successData that mirrors paid flow
          const builtSuccess = {
            success: true,
            message: data.message || 'Registration submitted successfully',
            registration_id: data.registration_id,
            registration_number: data.registration_id ? `REG${String(data.registration_id).padStart(6, '0')}` : null,
            training: {
              id: training.id,
              title: training.title,
              date: training.date,
              location: training.location,
              duration: training.duration,
              price: training.price
            },
            registration: {
              payment_status: 'pending',
              registration_status: 'confirmed'
            },
            email_status: data.email_status
          };

          setSuccessData(builtSuccess);
          setStep('success');
          if (onSuccess) {
            onSuccess(builtSuccess);
          }

          // Auto-close modal after short delay so user sees confirmation message
          setTimeout(() => {
            try { onClose(); } catch (e) { /* ignore */ }
          }, 3000);
        } else {
          throw new Error(data.detail || 'Registration failed');
        }
      } else {
        // Paid training - create Razorpay order
        const response = await fetch(`${API_URL}/api/payment/create-order`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...formData,
            training_id: training.id,
            experience_years: formData.experience_years ? parseInt(formData.experience_years) : null
          }),
        });

        const orderData = await response.json();

        if (response.ok) {
          // Open Razorpay payment modal
          await handlePayment(orderData);
        } else {
          throw new Error(orderData.detail || 'Failed to create payment order');
        }
      }
    } catch (error) {
      console.error('Registration error:', error);
      setError(error.message || 'Failed to submit registration. Please try again.');
      setStep('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <CardHeader className="relative">
          <button
            onClick={onClose}
            className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>

          <CardTitle className="text-2xl">
            {step === 'success' ? 'Registration Successful!' : 'Register for Training'}
          </CardTitle>
          <CardDescription>
            {step === 'success'
              ? 'Your registration has been confirmed.'
              : `${training?.title || 'Training'} - ${training?.is_paid ? training.price : 'Complimentary'}`
            }
          </CardDescription>
        </CardHeader>

        <CardContent>
          {step === 'success' && (
            <div className="text-center py-8">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Thank You!</h3>
              <p className="text-gray-600 mb-4">
                We've received your registration{training?.is_paid ? ' and payment' : ''}.
                <br />
                You will receive a confirmation email shortly.
              </p>

              {successData && (
                <div className="bg-gray-50 p-4 rounded-md text-left mt-4">
                  {successData.registration_number && (
                    <p className="text-sm font-mono text-gray-800 mb-1">Registration No: <span className="font-semibold">{successData.registration_number}</span></p>
                  )}

                  {successData.training && (
                    <div className="text-sm text-gray-700">
                      <div><strong>Training:</strong> {successData.training.title}</div>
                      {successData.training.date && <div><strong>Date:</strong> {new Date(successData.training.date).toLocaleString()}</div>}
                      {successData.training.location && <div><strong>Location:</strong> {successData.training.location}</div>}
                    </div>
                  )}
                </div>
              )}
              <Button onClick={onClose}>Close</Button>
            </div>
          )}

          {step === 'error' && (
            <div className="text-center py-8">
              <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Something went wrong</h3>
              <p className="text-gray-600 mb-4">{error}</p>
              <div className="flex gap-3 justify-center">
                <Button onClick={() => { setStep('form'); setError(null); }} variant="outline">
                  Try Again
                </Button>
                <Button onClick={onClose}>Close</Button>
              </div>
            </div>
          )}

          {step === 'processing' && (
            <div className="text-center py-12">
              <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
              <p className="text-gray-600">
                {training?.is_paid ? 'Processing payment...' : 'Submitting registration...'}
              </p>
            </div>
          )}

          {step === 'form' && (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                  {error}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter your full name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="+91 XXXXXXXXXX"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Organization
                  </label>
                  <input
                    type="text"
                    name="organization"
                    value={formData.organization}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Company or institution"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Designation
                  </label>
                  <input
                    type="text"
                    name="designation"
                    value={formData.designation}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Job title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Years of Experience
                  </label>
                  <input
                    type="number"
                    name="experience_years"
                    value={formData.experience_years}
                    onChange={handleChange}
                    min="0"
                    max="99"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Special Requirements
                  </label>
                  <textarea
                    name="special_requirements"
                    value={formData.special_requirements}
                    onChange={handleChange}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Any dietary restrictions, accessibility needs, etc."
                  />
                </div>
              </div>

              <div className="flex justify-between items-center pt-4 border-t">
                <Button type="button" onClick={onClose} variant="outline">
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      {training?.is_paid ? `Pay ${training.price}` : 'Register for Free'}
                    </>
                  )}
                </Button>
              </div>

              {training?.is_paid && (
                <p className="text-xs text-gray-500 text-center">
                  Secure payment powered by Razorpay. Your payment information is encrypted and secure.
                </p>
              )}
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
